---
title: "Careers page and the apply flow"
description: "How your public careers page works in Chosen: publishing jobs, branding the page, building application forms, and what candidates see when they apply."
category: "careers"
keywords:
  - "ATS careers page"
  - "job posting page"
  - "candidate apply flow"
  - "careers site"
schema: "HowTo"
nav:
  section: "guides"
  order: 50
---

Your careers page is the one part of Chosen that candidates actually see. Everything else is built for you; this is built for them. Every org gets one at `hire.chosenhq.com/careers/<your-org-slug>`, and it lists every job you've published.

This section covers the four things you control: whether the page and each job are visible, how the page looks, what you ask applicants, and what the apply flow feels like from the candidate's side.

## In this section

Read these in order if you're setting the page up for the first time. Each one stands alone if you just need to fix something.

<Steps>
  <li>[Publishing jobs and the careers page](/guides/careers/publish-jobs) — the page is off until you turn it on, and each job has its own publish switch. Start here.</li>
  <li>[Branding the careers page](/guides/careers/branding-the-page) — banner image, company description, and your URL slug.</li>
  <li>[Application questions](/guides/careers/application-questions) — the built-in fields, the custom question types, and why short forms win.</li>
  <li>[What candidates see](/guides/careers/what-candidates-see) — the apply flow, passwordless sign-in, and the dashboard returning candidates get.</li>
  <li>[Embedding jobs elsewhere](/guides/careers/embed-and-api) — linking to specific jobs from your marketing site.</li>
</Steps>

<Callout type="tip" title="Open your own careers page once a quarter">
It's the page candidates judge you on, and it's easy to forget it exists. Use the preview link in **Career Page Settings** to look at it the way an applicant would — including while it's still hidden.
</Callout>

If you haven't created a job yet, [post your first job](/getting-started/post-first-job) walks the basic flow. Applications land in your [candidates table](/guides/candidates) once they start arriving.
